'use client';

import { useState, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import ContactModal from './ContactModal';
import AiShapeLg from '../assets/AiShapeLg';
import AiShapeSm from '../assets/AiShapeSm';
import { gsap } from 'gsap';
import { trackEvent } from '@/lib/analytics';

export default function AiStickyButton() {
  const pathname = usePathname();
  const [isVisible, setIsVisible] = useState(false);
  const [triggerScrollY, setTriggerScrollY] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [originPosition, setOriginPosition] = useState<{ x: number; y: number } | undefined>();
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const touchHandledRef = useRef(false);
  const lgSvgRef = useRef<HTMLDivElement>(null);
  const smSvgRef = useRef<HTMLDivElement>(null);

  // Reset state when pathname changes
  useEffect(() => {
    setTriggerScrollY(null);
    setIsVisible(false);
    setIsFooterVisible(false);
  }, [pathname]);

  // Observe footer visibility to fade out button
  useEffect(() => {
    const footer = document.querySelector('footer');
    if (!footer) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFooterVisible(entry.isIntersecting);
      },
      {
        threshold: 0,
        rootMargin: '0px 0px 0px 0px', // Trigger as soon as footer enters viewport
      }
    );

    observer.observe(footer);

    return () => {
      observer.disconnect();
    };
  }, [pathname]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Find the "Our Work" section (case-studies-home) - only on homepage
      const ourWorkSection = document.querySelector('.case-studies-home');
      
      if (!ourWorkSection) {
        // Fallback for other pages: show button after scrolling 60vh
        const viewportHeight = window.innerHeight;
        const triggerPoint = viewportHeight * 0.6; // 60vh
        if (currentScrollY > triggerPoint && triggerScrollY === null) {
          setTriggerScrollY(triggerPoint);
          setIsVisible(true);
        } else if (triggerScrollY !== null) {
          // Already triggered - stay visible if we're below the trigger point
          setIsVisible(currentScrollY >= triggerScrollY);
        } else {
          // Not yet triggered
          setIsVisible(currentScrollY >= triggerPoint);
        }
        return;
      }
      
      // Homepage logic: show button when the section is near the top
      const sectionRect = ourWorkSection.getBoundingClientRect();
      
      // Show button when the section is near the top of the viewport (within 12.5rem/200px from top)
      const isNearTop = sectionRect.top <= 200 && sectionRect.bottom > 0;
      
      if (isNearTop && triggerScrollY === null) {
        // First time reaching the trigger point - record the scroll position
        setTriggerScrollY(currentScrollY);
        setIsVisible(true);
      } else if (triggerScrollY !== null) {
        // Already triggered - stay visible if we're below the trigger point
        setIsVisible(currentScrollY >= triggerScrollY);
      } else {
        // Not yet triggered
        setIsVisible(false);
      }
    };

    // Check on mount and on scroll
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [triggerScrollY]);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Prevent click if touch was already handled
    if (touchHandledRef.current) {
      touchHandledRef.current = false;
      return;
    }
    // Track AI button click
    trackEvent('ai_click', {
      click_text: 'what_are_you_solving_for',
      page_path: pathname
    });
    // Capture the exact mouse click position
    setOriginPosition({ x: e.clientX, y: e.clientY });
    setIsModalOpen(true);
    // Dispatch event to hide navigation
    window.dispatchEvent(new CustomEvent('contactModalOpen'));
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLButtonElement>) => {
    // Prevent hover state on touch devices
    setIsHovered(false);
    // Capture touch position from touchstart
    const touch = e.touches[0];
    if (touch) {
      // Mark touch as handled immediately to prevent click event
      touchHandledRef.current = true;
    }
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLButtonElement>) => {
    // Prevent default to stop click event from firing
    e.preventDefault();
    // Capture touch position from touchend (changedTouches has the touch that ended)
    const touch = e.changedTouches[0];
    if (touch) {
      // Track AI button click
      trackEvent('ai_click', {
        click_text: 'what_are_you_solving_for',
        page_path: pathname
      });
      setOriginPosition({ x: touch.clientX, y: touch.clientY });
      setIsModalOpen(true);
      // Dispatch event to hide navigation
      window.dispatchEvent(new CustomEvent('contactModalOpen'));
      // Reset after a short delay
      setTimeout(() => {
        touchHandledRef.current = false;
      }, 300);
    }
  };

  // Scale animation effect
  useEffect(() => {
    if (isVisible) {
      // Ensure initial state is rendered first, then start animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setShouldAnimate(true);
        });
      });
    } else {
      setShouldAnimate(false);
    }
  }, [isVisible]);

  // GSAP animation for large SVG - runs every 5 seconds
  useEffect(() => {
    if (!isVisible || !lgSvgRef.current || !smSvgRef.current) return;

    // Set transform origin to center for both SVGs - this must be set in GSAP
    gsap.set([lgSvgRef.current, smSvgRef.current], { 
      transformOrigin: '50% 50%'
    });

    const animation = gsap.timeline({ repeat: -1, repeatDelay: 6.5 });
    
    animation
      .to(smSvgRef.current, {
        scale: 0,
        rotation: '+=90',
        duration: 0.1,
        ease: 'power2.in'
      })
      .to(lgSvgRef.current, {
        scale: 0,
        rotation: '+=90',
        duration: 0.3,
        ease: 'power2.in'
      }, '<')
      .to(lgSvgRef.current, {
        scale: 1,
        rotation: 360,
        duration: 0.8,
        ease: 'back.out(1.2)'
      }, '>0.3')
      .to(smSvgRef.current, {
        scale: 1,
        duration: 0.4,
        rotation: '+=90',
        ease: 'back.out(1.7)'
      }, '<0.65');

    return () => {
      animation.kill();
    };
  }, [isVisible]);

  const buttonContainerStyle: React.CSSProperties = {
    transition: 'transform 500ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 400ms ease-out',
    transform: shouldAnimate ? 'scale(1)' : 'scale(0)',
    transformOrigin: 'center',
    opacity: isFooterVisible ? 0 : 1,
    pointerEvents: isFooterVisible ? 'none' : 'auto',
  };

  return (
    <>
      {isVisible && (
        <div
          className="ai-sticky-button fixed bottom-5 md:bottom-8 right-5 md:right-8 z-50"
          style={{ ...buttonContainerStyle }}
        >
          <button
            onClick={handleClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(e as any); } }}
            onMouseEnter={() => !isFooterVisible && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            disabled={isFooterVisible}
            aria-disabled={isFooterVisible}
            className="transition-all duration-500 ease-out group block ml-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-full"
            aria-label="Open contact modal"
          >
            <div 
              className={`rounded-full transition-all duration-250 ease-in-out relative h-[3.75rem] ${
                isHovered ? 'w-[19rem] justify-start' : 'w-[3.75rem] justify-center'
              }`}
              style={{
                background: isHovered 
                  ? 'radial-gradient(circle at 30% 30%, #6BA5D6 0%, #5B9FD8 20%, #7BC5A8 60%, #A4E5A0 100%)'
                  : 'transparent',
                padding: isHovered ? '2px' : '0px',
                transition: 'width 250ms cubic-bezier(0.4, 0, 0.2, 1), padding 250ms cubic-bezier(0.4, 0, 0.2, 1), height 250ms cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              {/* Inner container with eggshell background */}
              <div
                className="h-full rounded-full flex items-center overflow-hidden relative"
                style={{
                  background: isHovered ? '#FDFEF6' : 'transparent',
                  width: '100%'
                }}
              >
                {/* Original gradient background that fades out and scales up */}
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: 'radial-gradient(circle at 30% 30%, #6BA5D6 0%, #5B9FD8 20%, #7BC5A8 60%, #A4E5A0 100%)',
                    opacity: isHovered ? 0 : 1,
                    transform: isHovered ? 'scale(1.2)' : 'scale(1)',
                    transition: 'opacity 250ms cubic-bezier(0.4, 0, 0.2, 1), transform 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                    pointerEvents: 'none',
                    zIndex: 0
                  }}
                />
                <div className={`animated-ai-shape flex-shrink-0 relative z-10 flex items-center justify-center w-[3.75rem] h-[3.75rem] transition-colors duration-250 ease-in-out overflow-visible ${isHovered ? 'text-black' : 'text-white'}`} style={{ zIndex: 1 }}>
                  <div ref={lgSvgRef} className="absolute inset-0 flex items-center justify-center ai-sticky-button-lg-svg" style={{ transformOrigin: 'center center' }}>
                    <div className="w-[1.625rem] h-[1.625rem]">
                      <AiShapeLg />
                    </div>
                  </div>
                  <div ref={smSvgRef} className="absolute flex items-center justify-center ai-sticky-button-sm-svg" style={{ transformOrigin: 'center center' }}>
                    <div className="w-[0.625rem] h-[0.625rem]">
                      <AiShapeSm />
                    </div>
                  </div>
                </div>
                {isHovered && (
                  <span 
                    key="text"
                    className="text-black body-sm whitespace-nowrap animate-fade-in"
                    style={{
                      animation: 'fadeIn 400ms ease-out 150ms forwards',
                      opacity: 0
                    }}
                  >
                    What are you solving for?
                  </span>
                )}
                <style jsx>{`
                  @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                  }
                `}</style>
              </div>
            </div>
          </button>
        </div>
      )}
      
      <ContactModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        originPosition={originPosition}
      />
    </>
  );
}
